<template>
  <div class="ig-employee-list-page">
    <div class="page-container">
      <header class="page-header">
        <div>
          <div class="page-title">
            <span class="title-mark"></span>
            入职员工信息
          </div>
          <p>查看已填写的普工入职资料及提交状态</p>
        </div>
        <div class="header-actions">
          <el-button icon="el-icon-edit-outline" @click="goToForm">
            返回填写页
          </el-button>
          <el-button
            type="success"
            icon="el-icon-refresh"
            :loading="loading"
            @click="refreshList"
          >
            刷新
          </el-button>
        </div>
      </header>

      <section class="overview-card">
        <div class="overview-icon">
          <i class="el-icon-user-solid"></i>
        </div>
        <div>
          <div class="overview-label">已提交员工</div>
          <div class="overview-value">
            {{ total }}
            <span>人</span>
          </div>
        </div>
      </section>

      <section class="filter-card">
        <div class="filter-title">
          <i class="el-icon-search"></i>
          筛选条件
        </div>
        <el-form
          class="filter-form"
          :model="filterForm"
          label-position="top"
          @submit.native.prevent
        >
          <el-form-item label="姓名">
            <el-input
              v-model.trim="filterForm.Title"
              clearable
              placeholder="请输入姓名"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="手机号码">
            <el-input
              v-model.trim="filterForm.MobileNo"
              clearable
              maxlength="11"
              placeholder="请输入手机号码"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="推送状态">
            <el-select
              v-model="filterForm.isPush"
              clearable
              placeholder="全部状态"
            >
              <el-option label="已推送" value="1" />
              <el-option label="未推送" value="0" />
            </el-select>
          </el-form-item>
          <el-form-item label="派遣公司">
            <el-select
              v-model="filterForm.PqCompany"
              :loading="optionsLoading"
              placeholder="请选择派遣公司"
              clearable
              filterable
            >
              <el-option
                v-for="item in dispatchCompanyOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="是否派遣工">
            <el-select
              v-model="filterForm.isPq"
              clearable
              placeholder="全部"
            >
              <el-option label="是" value="1" />
              <el-option label="否" value="0" />
            </el-select>
          </el-form-item>
          <el-form-item class="date-range-item" label="填写时间">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
            />
          </el-form-item>
          <div class="filter-actions">
            <el-button
              type="success"
              icon="el-icon-search"
              :loading="loading"
              @click="handleSearch"
            >
              查询
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetFilters">
              重置
            </el-button>
          </div>
        </el-form>
      </section>

      <section class="table-card">
        <div class="table-toolbar">
          <div>
            <h2>员工资料列表</h2>
            <p>勾选员工可批量打印登记表，点击“查看详情”可查看完整资料</p>
          </div>
          <div class="toolbar-actions">
            <el-button
              type="success"
              icon="el-icon-position"
              :loading="pushLoading"
              :disabled="selectedRows.length === 0"
              @click="pushSelected"
            >
              推送选中（{{ selectedRows.length }}）
            </el-button>
            <el-button
              type="primary"
              icon="el-icon-printer"
              :disabled="selectedRows.length === 0"
              @click="printSelected"
            >
              打印选中（{{ selectedRows.length }}）
            </el-button>
            <span class="total-tag">共 {{ total }} 条</span>
          </div>
        </div>

        <div class="table-scroll">
          <el-table
            v-loading="loading"
            class="employee-table"
            :data="tableData"
            border
            stripe
            empty-text="暂无员工填写记录"
            @selection-change="handleSelectionChange"
          >
            <el-table-column type="selection" width="60" align="center" />
            <el-table-column
              type="index"
              label="序号"
              width="70"
              align="center"
              :index="getRowIndex"
            />
            <el-table-column
              prop="Title"
              label="姓名"
              min-width="110"
              align="center"
              show-overflow-tooltip
            />
            <el-table-column
              prop="IDNo"
              label="身份证号"
              min-width="190"
              align="center"
              show-overflow-tooltip
            >
              <template slot-scope="{ row }">
                {{ displayValue(row.IDNo) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="ApplyID"
              label="手机号"
              min-width="130"
              align="center"
            />
            <el-table-column
              prop="created"
              label="提交时间"
              min-width="170"
              align="center"
            >
              <template slot-scope="{ row }">
                {{ formatCreated(row.created) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="isPush"
              label="推送状态"
              width="105"
              align="center"
            >
              <template slot-scope="{ row }">
                <el-tag
                  :type="Number(row.isPush) === 1 ? 'success' : 'info'"
                  size="small"
                >
                  {{ Number(row.isPush) === 1 ? "已推送" : "未推送" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              label="操作"
              width="245"
              align="center"
              fixed="right"
            >
              <template slot-scope="{ row }">
                <el-button
                  type="success"
                  plain
                  size="mini"
                  icon="el-icon-document"
                  @click="goToDetail(row)"
                >
                  查看详情
                </el-button>
                <!-- 未推送才允许编辑 -->
                <el-button
                  type="primary"
                  plain
                  size="mini"
                  icon="el-icon-edit"
                  @click="goToEdit(row)"
                  v-if="row.isPush!=1"
                >
                  编辑
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div v-if="total > 0" class="pagination-wrapper">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="currentPage"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="total"
            @size-change="handlePageSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import api from "@/apis";

export default {
  name: "EmployeeInformationList",
  data() {
    return {
      loading: false,
      pushLoading: false,
      optionsLoading: false,
      dispatchCompanyOptions: [],
      tableData: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      selectedRows: [],
      dateRange: [],
      filterForm: {
        Title: "",
        isPush: "",
        MobileNo: "",
        PqCompany: "",
        isPq: "",
      },
    };
  },
  created() {
    this.loadDispatchCompanyOptions();
    this.fetchList();
  },
  methods: {
    // 加载派遣公司下拉选项
    async loadDispatchCompanyOptions() {
      this.optionsLoading = true;
      try {
        const res = await api.getPreEmployeeOptions({ type: "pqcompany" });
        const data = res.data || [];
        // 兼容直接返回字符串数组的情况
        if (Array.isArray(data) && data.every((item) => typeof item === "string")) {
          this.dispatchCompanyOptions = data
            .filter(Boolean)
            .map((item) => ({ value: item, label: item }));
        } else {
          this.dispatchCompanyOptions = Object.values(data)
            .map((item) => {
              const value = item.ID || item.orgid;
              const label = String(item.Title || item.title || "")
                .replace(/\r?\n/g, "")
                .trim();
              return { value: value ? String(value) : "", label };
            })
            .filter((item) => item.value && item.label);
        }
      } catch (error) {
        this.dispatchCompanyOptions = [];
      } finally {
        this.optionsLoading = false;
      }
    },
    displayValue(value) {
      return value === null || value === undefined || value === ""
        ? "-"
        : value;
    },
    formatCreated(value) {
      if (!value || String(value).startsWith("0000-00-00")) return "-";
      return value;
    },
    getRowIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await api.getPreEmployeeList({
          page: this.currentPage,
          pagesize: this.pageSize,
          ...this.getFilterParams(),
        });
        const pageData = res.data || {};
        this.tableData = Array.isArray(pageData.data) ? pageData.data : [];
        this.total = Number(pageData.total) || 0;
        this.pageSize = Number(pageData.per_page) || this.pageSize;
        this.selectedRows = [];
      } catch (error) {
        this.tableData = [];
        this.total = 0;
        this.$message.error("员工信息列表加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    refreshList() {
      this.currentPage = 1;
      this.fetchList();
    },
    // 仅提交已填写的筛选值，参数名称与列表接口保持一致
    getFilterParams() {
      const params = Object.keys(this.filterForm).reduce((result, key) => {
        const value = this.filterForm[key];
        if (value !== null && value !== undefined && value !== "") {
          result[key] = typeof value === "string" ? value.trim() : value;
        }
        return result;
      }, {});
      if (this.dateRange && this.dateRange.length === 2) {
        params.startdate = this.dateRange[0];
        params.enddate = this.dateRange[1];
      }
      return params;
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchList();
    },
    resetFilters() {
      this.dateRange = [];
      this.filterForm = {
        Title: "",
        isPush: "",
        MobileNo: "",
        PqCompany: "",
        isPq: "",
      };
      this.currentPage = 1;
      this.fetchList();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchList();
    },
    // 修改每页条数后从第一页重新获取，接口参数名为 pagesize
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchList();
    },
    // 保存当前页勾选的员工，用于批量打印登记表
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    // 将选中员工的预入职信息 ID 以逗号分隔后批量推送
    async pushSelected() {
      const applyIds = this.selectedRows
        .map((row) => String(row.ApplyID || "").trim())
        .filter(Boolean);
      if (!applyIds.length) {
        this.$message.warning("请先勾选需要推送的员工");
        return;
      }
      try {
        await this.$confirm(
          `确定推送选中的 ${applyIds.length} 名员工信息吗？`,
          "批量推送确认",
          {
            confirmButtonText: "确定推送",
            cancelButtonText: "取消",
            type: "warning",
          }
        );
      } catch (error) {
        return;
      }
      this.pushLoading = true;
      try {
        const res = await api.pushPreEmployees({
          applyids: applyIds.join(","),
        });
        this.$message.success(res.message || res.msg || "员工信息推送成功");
        await this.fetchList();
      } catch (error) {
        this.$message.error(error.message || "员工信息推送失败，请稍后重试");
      } finally {
        this.pushLoading = false;
      }
    },
    printSelected() {
      const applyIds = this.selectedRows
        .map((row) => String(row.id || "").trim())
        .filter(Boolean);
      if (!applyIds.length) {
        this.$message.warning("请先勾选需要打印的员工");
        return;
      }
      const printRoute = this.$router.resolve({
        path: "/employee/print",
        query: {
          ids: applyIds.join(","),
        },
      });
      const printWindow = window.open(printRoute.href, "_blank");
      if (!printWindow) {
        this.$router.push(printRoute.route.fullPath);
      }
    },
    goToForm() {
      this.$router.push("/employee/info");
    },
    goToDetail(row) {
      this.$router.push({
        path: "/employee/detail",
        query: {
          id: String(row.id),
        },
      });
    },
    // 携带预入职 ID 进入五步表单编辑已有资料
    goToEdit(row) {
      this.$router.push({
        path: "/employee/info",
        query: {
          id: String(row.id),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ig-employee-list-page {
  width: 100%;
  min-height: 100vh;
  padding: 28px;
  font-size: 22px;
  color: #303133;
  background: #f3f6f5;
  box-sizing: border-box;
}

/* PC 端使用全部可用宽度，不限制内容最大宽度 */
.page-container {
  width: 100%;
  margin: 0;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 104px;
  padding: 22px 30px;
  color: #fff;
  background: linear-gradient(135deg, #178a50, #20aa68);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(20, 126, 74, 0.18);
  box-sizing: border-box;

  p {
    margin: 8px 0 0 18px;
    font-size: 20px;
    color: rgba(255, 255, 255, 0.82);
  }
}

.page-title {
  display: flex;
  align-items: center;
  font-size: 30px;
  font-weight: 600;
}

.title-mark {
  width: 5px;
  height: 27px;
  margin-right: 13px;
  background: #fff;
  border-radius: 3px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.overview-card {
  display: flex;
  align-items: center;
  width: 260px;
  min-height: 104px;
  margin-top: 20px;
  padding: 20px 24px;
  background: #fff;
  border: 1px solid #e5ece8;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(31, 67, 49, 0.06);
  box-sizing: border-box;
}

.overview-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-right: 18px;
  font-size: 30px;
  color: #1fa463;
  background: #e9f7f0;
  border-radius: 14px;
}

.overview-label {
  margin-bottom: 5px;
  font-size: 20px;
  color: #84918a;
}

.overview-value {
  font-size: 34px;
  font-weight: 600;
  color: #25352d;

  span {
    margin-left: 3px;
    font-size: 20px;
    font-weight: 400;
    color: #84918a;
  }
}

.filter-card {
  margin-top: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5ece8;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(31, 67, 49, 0.06);
}

.filter-title {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 26px;
  font-weight: 600;
  color: #25352d;

  i {
    margin-right: 10px;
    color: #1fa463;
  }
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  align-items: end;
  gap: 18px 20px;
}

.date-range-item {
  grid-column: span 2;
}

.filter-actions {
  display: flex;
  align-items: center;
  min-height: 52px;
  gap: 12px;

  .el-button {
    flex: 1;
    min-width: 100px;
    margin: 0;
  }
}

::v-deep .filter-form .el-form-item {
  margin: 0;
}

::v-deep .filter-form .el-form-item__label {
  padding-bottom: 8px;
  font-size: 22px;
  line-height: 30px;
  color: #45534c;
}

::v-deep .filter-form .el-input,
::v-deep .filter-form .el-select,
::v-deep .filter-form .el-date-editor {
  width: 100%;
}

::v-deep .filter-form .el-input__inner {
  height: 52px;
  font-size: 22px;
  line-height: 52px;
}

::v-deep .filter-form .el-input__icon {
  font-size: 22px;
  line-height: 52px;
}

::v-deep .filter-form .el-date-editor--daterange {
  height: 52px;
  padding: 3px 14px;
}

::v-deep .filter-form .el-range-input,
::v-deep .filter-form .el-range-separator {
  font-size: 22px;
}

::v-deep .filter-form .el-range-separator {
  width: 44px;
  line-height: 44px;
}

::v-deep .filter-form .el-button {
  height: 52px;
  padding: 0 20px;
  font-size: 22px;
}

.table-card {
  margin-top: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5ece8;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(31, 67, 49, 0.06);
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #25352d;
  }

  p {
    margin: 7px 0 0;
    font-size: 20px;
    color: #909a94;
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.total-tag {
  padding: 6px 13px;
  font-size: 20px;
  color: #1b9258;
  background: #eaf7f0;
  border-radius: 16px;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
}

.employee-table {
  width: 100%;
  min-width: 1050px;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 22px;
}

::v-deep .employee-table th {
  color: #45534c;
  background: #f5f8f6;
}

/* 放大 PC 表格及操作按钮文字，提升宽屏阅读体验 */
::v-deep .employee-table .cell {
  font-size: 20px;
  line-height: 30px;
}

/* 单独放大表格正文数据，包含普通列和固定操作列 */
::v-deep .employee-table .el-table__body .cell {
  font-size: 24px !important;
  line-height: 34px;
}

::v-deep .employee-table .el-table__body td {
  padding: 14px 0;
}

::v-deep .page-header .el-button,
::v-deep .toolbar-actions .el-button,
::v-deep .employee-table .el-button {
  font-size: 20px;
}

::v-deep .toolbar-actions .el-button {
  padding: 12px 18px;
}

::v-deep .employee-table .el-checkbox__inner {
  width: 22px;
  height: 22px;
}

::v-deep .employee-table .el-checkbox__inner::after {
  top: 3px;
  left: 7px;
  width: 5px;
  height: 11px;
}

/* Element UI 小尺寸组件需要同步调整高度，防止大字体被裁切 */
::v-deep .employee-table .el-tag {
  height: 38px;
  font-size: 22px;
  line-height: 36px;
}

::v-deep .employee-table .el-button {
  padding: 8px 12px;
}

::v-deep .el-pagination,
::v-deep .el-pagination button,
::v-deep .el-pagination span:not([class*="suffix"]),
::v-deep .el-pager li {
  font-size: 20px;
}

::v-deep .el-pagination button,
::v-deep .el-pager li {
  min-width: 36px;
  height: 36px;
  line-height: 36px;
}

::v-deep .el-pagination .el-input__inner {
  height: 36px;
  font-size: 20px;
  line-height: 36px;
}

::v-deep .el-pagination .el-select .el-input {
  width: 130px;
}

::v-deep .el-pagination__jump {
  height: 36px;
  font-size: 20px;
  line-height: 36px;
}

::v-deep .employee-table .el-button--success.is-plain {
  color: #198f55;
  border-color: #8bd0ae;
  background: #eef9f3;
}

/* 防止加载遮罩淡出后继续拦截表格操作 */
::v-deep .el-loading-mask.el-loading-fade-leave-active {
  pointer-events: none;
}

@media (max-width: 768px) {
  .ig-employee-list-page {
    padding: 14px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
    padding: 20px;
  }

  .page-title {
    font-size: 22px;
  }

  .header-actions {
    width: 100%;
    margin-top: 18px;

    .el-button {
      flex: 1;
      margin: 0;
    }
  }

  .overview-card {
    width: 100%;
  }

  .filter-card {
    padding: 18px 14px;
  }

  .filter-form {
    grid-template-columns: 1fr;
  }

  .date-range-item {
    grid-column: span 1;
  }

  .table-card {
    padding: 18px 14px;
  }

  .table-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .table-toolbar p {
    max-width: 230px;
  }

  .toolbar-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .toolbar-actions .el-button {
    margin: 0;
  }

  .total-tag {
    align-self: flex-start;
  }

  .pagination-wrapper {
    justify-content: center;
    overflow-x: auto;
  }
}
</style>
